import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/App";

const PUBLISHER_ID = process.env.REACT_APP_ADSENSE_PUBLISHER_ID || "ca-pub-7487594011241926";
const SLOT_BANNER = process.env.REACT_APP_ADSENSE_SLOT_BANNER || "";
const SLOT_INFEED = process.env.REACT_APP_ADSENSE_SLOT_INFEED || "";

/**
 * Google AdSense banner. Free-tier only; Pro users render NOTHING (zero footprint).
 *
 * Behavioural notes:
 *  - Lazy-loaded via IntersectionObserver — the underlying `<ins>` only
 *    activates once the wrapper scrolls within 200px of the viewport. Saves
 *    first-paint cost for users who never reach the ad.
 *  - CLS-safe: a fixed `min-height` placeholder reserves layout space before
 *    AdSense responds.
 *  - Fail-silent: ad blockers, network errors, or a missing slot id all just
 *    render an invisible spacer — the surrounding page is unaffected.
 *  - Touch-safe: until the ad has actually FILLED (data-ad-status="filled"),
 *    the wrapper sets `pointer-events: none` so an empty AdSense iframe can't
 *    steal taps from interactive content sitting above/below it. This was
 *    the original Map bug — keep this behaviour.
 *  - StrictMode-safe: `pushed.current` guards against the dev-mode double-mount
 *    that would otherwise trip AdSense's "already have ads" warning.
 *
 * Slot ids come from env vars (`REACT_APP_ADSENSE_SLOT_BANNER`,
 * `REACT_APP_ADSENSE_SLOT_INFEED`). Until you create the units in the
 * AdSense dashboard and paste the ids in `frontend/.env`, the components
 * render nothing — the screen looks identical to the pre-AdSense layout
 * (no surprise blanks, no broken flex spacing).
 *
 * @param {string} slot - Slot id override. If unset, falls back to the
 *                        `format`-appropriate env var.
 * @param {string} format - "auto" (default — responsive banner) or "fluid"
 *                          (in-article native style — pair with `layout`).
 * @param {string} layout - e.g. "in-article" for native in-feed.
 * @param {number} minHeight - Reserved px height for CLS prevention.
 * @param {string} className - Tailwind classes for the wrapper.
 */
export default function AdBanner({
  slot: slotProp,
  format = "auto",
  layout,
  minHeight = 100,
  className = "",
  testId = "ad-banner",
}) {
  const { user } = useAuth();
  const wrapperRef = useRef(null);
  const insRef = useRef(null);
  const pushedRef = useRef(false);
  const [inView, setInView] = useState(false);
  const [filled, setFilled] = useState(false);

  const isPro = user?.subscription_status === "pro";
  // Pick the right slot id automatically based on `format` so callers don't
  // need to repeat env-var plumbing at every call site.
  const slot = slotProp || (format === "fluid" ? SLOT_INFEED : SLOT_BANNER);
  const noSlot = !slot || !PUBLISHER_ID;

  // Lazy load via IntersectionObserver
  useEffect(() => {
    if (isPro || noSlot) return;
    const node = wrapperRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [isPro, noSlot]);

  // Push to AdSense once the slot is mounted and visible
  useEffect(() => {
    if (!inView || pushedRef.current || !insRef.current) return;
    pushedRef.current = true;
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (err) {
      // Adblocker / network — silent.
    }
    // Poll for fill. AdSense annotates `data-ad-status="filled"` on the
    // <ins> when it actually paints. We re-check a few times because the
    // network call is async and a single check can miss it.
    let cancelled = false;
    const poll = () => {
      if (cancelled || !insRef.current) return;
      const status = insRef.current.getAttribute("data-ad-status");
      if (status === "filled") {
        setFilled(true);
        return;
      }
      setTimeout(poll, 1500);
    };
    const t = setTimeout(poll, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [inView]);

  if (isPro) return null;
  if (noSlot) return null;

  return (
    <div
      ref={wrapperRef}
      className={`ad-banner-container overflow-hidden ${className}`}
      data-testid={testId}
      style={{
        // Until filled, occupy the reserved height (CLS-safe) but ignore
        // pointer events — see Map-marker bug discussion above.
        minHeight: filled ? undefined : minHeight,
        pointerEvents: filled ? "auto" : "none",
      }}
    >
      {inView && (
        <ins
          ref={insRef}
          className="adsbygoogle"
          style={{ display: "block", minHeight }}
          data-ad-client={PUBLISHER_ID}
          data-ad-slot={slot}
          data-ad-format={format}
          data-full-width-responsive="true"
          {...(layout ? { "data-ad-layout": layout } : {})}
        />
      )}
    </div>
  );
}

/**
 * In-feed (native, in-article) variant — placed BETWEEN list items in the
 * Nearby and Notifications screens. Uses the in-article slot from env.
 */
export function InFeedAd({ className = "", testId = "in-feed-ad" }) {
  return (
    <AdBanner
      format="fluid"
      layout="in-article"
      minHeight={140}
      className={className}
      testId={testId}
    />
  );
}
